/**
 * DocumentCanvas - Fixed-size document wrapper with zoom support
 * Provides print-fidelity document sizing for Letter, Legal, Half Letter, and Wristband formats
 * Pattern follows presentation editor's aspect-ratio locked canvas
 */

import { CallSheetCanvas } from './DocumentSectionCanvas'
import type { SectionItem, PlayItem } from './Types'
import type { DocumentCanvasPreset } from '@/types/BlockProperties'
import './document-canvas.css'

interface DocumentCanvasProps {
	canvasPreset: DocumentCanvasPreset
	sections: SectionItem[]
	plays: PlayItem[]
	zoomLevel: number
	showGrid: boolean
	selectedSectionId: string | null
	onSelectSection: (sectionId: string | null) => void
	onMoveSection: (sectionId: string, position: { x: number; y: number }) => void
	onAddPlayToSection: (sectionId: string, playId: string, columnIndex: number, rowIndex: number) => void
	onRemovePlay: (playId: string) => void
}

/**
 * Document size constants (96 DPI)
 */
export const DOCUMENT_SIZES = {
	letter: { width: 816, height: 1056, label: 'Letter (8.5" x 11")' },
	legal: { width: 816, height: 1344, label: 'Legal (8.5" x 14")' },
	half_letter: { width: 528, height: 816, label: 'Half Letter (5.5" x 8.5")' },
	wristband: { width: 240, height: 336, label: 'Wristband (2.5" x 3.5")' },
} as const

export function DocumentCanvas({
	canvasPreset,
	sections,
	plays,
	zoomLevel,
	showGrid,
	selectedSectionId,
	onSelectSection,
	onMoveSection,
	onAddPlayToSection,
	onRemovePlay,
}: DocumentCanvasProps) {
	const sizeClass = `document-canvas--${canvasPreset.replace('_', '-')}`

	return (
		<div
			className={`document-canvas ${sizeClass}`}
			style={{
				transform: `scale(${zoomLevel})`,
			}}
		>
			<CallSheetCanvas
				sections={sections}
				plays={plays}
				zoomLevel={100}
				showGrid={showGrid}
				selectedSectionId={selectedSectionId}
				onSelectSection={onSelectSection}
				onMoveSection={onMoveSection}
				onAddPlayToSection={onAddPlayToSection}
				onRemovePlay={onRemovePlay}
			/>
		</div>
	)
}
